import { Injectable, inject } from '@angular/core';
import { DynamicFieldConfig } from '../interfaces';
import { ConfirmationFeedbackService } from '../../../confirmation-feedback/confirmation-feedback.service';
import { Router } from '@angular/router';
import { isValidTimeRange, timeToMinutes } from 'src/app/shared/utils';

@Injectable({
    providedIn: 'root',
})
export class FormConfigService {
    private feedbackService = inject(ConfirmationFeedbackService);
    private router = inject(Router);
    /**
     * Aplica validações e máscaras automáticas aos campos
     */
    configureForm(config: DynamicFieldConfig[]): DynamicFieldConfig[] {
        return config.map(field => {
            const enhancedField = { ...field };

            // Aplica máscaras automáticas
            if (!field.mask) {
                const autoMask = this.getAutoMask(field.type);
                if (autoMask) {
                    enhancedField.mask = autoMask;
                }
            }

            // Aplica validações específicas por nome de campo
            if (field.name === 'plate') {
                enhancedField.validations = {
                    ...enhancedField.validations,
                    custom: this.createPlateValidator(),
                };
                enhancedField.errorMessages = {
                    ...enhancedField.errorMessages,
                    custom: 'Placa inválida, exemplo: AAA0000 ou AAA0A00',
                };
            }

            if (field.name === 'timeFinal') {
                enhancedField.validations = {
                    ...enhancedField.validations,
                    custom: this.createTimeValidator(),
                };
                enhancedField.errorMessages = {
                    ...enhancedField.errorMessages,
                    custom: 'Hora final deve ser posterior à hora inicial',
                };
            }

            if (field.name === 'kmFinal') {
                enhancedField.validations = {
                    ...enhancedField.validations,
                    custom: this.createKmFinalValidator(),
                };
                enhancedField.errorMessages = {
                    ...enhancedField.errorMessages,
                    custom: 'Km final deve ser maior que km inicial',
                };
            }

            return enhancedField;
        });
    }

    /**
     * Obtém máscara automática para o tipo de campo
     */
    private getAutoMask(fieldType: string): string | undefined {
        const masks = {
            cpf: '000.000.000-00',
            cnpj: '00.000.000/0000-00',
            cep: '00000-000',
            phone: '(00) 00000-0000',
            date: '00/00/0000',
            time: '00:00',
            plate: 'AAA0000',
            currency: 'R$ 0,00',
        };

        return masks[fieldType as keyof typeof masks];
    }

    /**
     * Validador para placa de veículo
     */
    private createPlateValidator() {
        return (control: any) => {
            const value = control.value;
            if (!value) return null;

            const plateRegex = /^[A-Z]{3}[0-9][0-9A-Z][0-9]{2}$/;
            const formattedPlate = value.toUpperCase().replace(/[^A-Z0-9]/g, '');

            return plateRegex.test(formattedPlate) ? null : { invalidPlate: true };
        };
    }

    /**
     * Validador para horário final (formato HH:MM)
     * Considera turnos que cruzam a meia-noite (ex: 18h às 6h)
     */
    private createTimeValidator() {
        return (control: any) => {
            const value = control.value;
            if (!value) return null;

            const form = control.parent;
            if (form) {
                const timeInitial = form.get('timeInitial')?.value;
                const workShift = form.get('workShift')?.value;

                if (timeInitial) {
                    // Verifica se é válido baseado no turno
                    const isValid = isValidTimeRange(timeInitial, value, workShift);
                    return isValid ? null : { invalidTime: true };
                }
            }

            return null;
        };
    }

    /**
     * Converte string de tempo (HH:MM) para minutos
     * @deprecated Use timeToMinutes from utils instead
     */
    private timeStringToMinutes(timeString: string): number {
        return timeToMinutes(timeString);
    }

    private createKmFinalValidator() {
        return (control: any) => {
            const value = control.value;
            if (!value) return null;

            const form = control.parent;
            if (form) {
                const kmInitial = form.get('kmInitial')?.value;
                if (kmInitial) {
                    return +value >= +kmInitial ? null : { invalidKmFinal: true };
                }
            }

            return null;
        };
    }

    // ========================================
    // MÉTODOS DE FEEDBACK
    // ========================================

    /**
     * Submete formulário com feedback automático
     */
    async submitFormWithFeedback<T>(
        formData: T,
        submitFn: (data: T) => Promise<void>,
        options: {
            successMessage: string;
            errorMessage?: string;
            duration?: number;
            onSuccess?: () => void;
            onError?: (error: any) => void;
        }
    ): Promise<void> {
        try {
            await submitFn(formData);

            // Mostra feedback de sucesso
            await this.feedbackService.show(options.successMessage, options.onSuccess, options.duration);
        } catch (error) {
            const errorMessage = options.errorMessage || 'Erro ao processar formulário';

            // Mostra feedback de erro
            await this.feedbackService.show(
                errorMessage,
                () => {
                    if (options.onError) options.onError(error);
                },
                options.duration
            );

            throw error;
        }
    }

    /**
     * Submete formulário com delay simulado (para desenvolvimento)
     */
    async submitFormWithDelay<T>(
        formData: T,
        submitFn: (data: T) => Promise<void>,
        options: {
            successMessage: string;
            errorMessage?: string;
            delay?: number;
            duration?: number;
            onSuccess?: () => void;
            onError?: (error: any) => void;
        }
    ): Promise<void> {
        const delay = options.delay ?? 2000;

        return this.submitFormWithFeedback(
            formData,
            async data => {
                // Simula delay de processamento
                await new Promise(resolve => setTimeout(resolve, delay));
                await submitFn(data);
            },
            options
        );
    }

    /**
     * Opções padrão para relatórios
     */
    getDefaultRelatorioOptions(navigationPath: string = '/'): {
        successMessage: string;
        errorMessage: string;
        delay: number;
        duration: number;
        onSuccess: () => void;
    } {
        return {
            successMessage: 'Relatório salvo com sucesso!',
            errorMessage: 'Erro ao salvar relatório',
            delay: 2000,
            duration: 3000,
            onSuccess: () => {
                this.router.navigate([navigationPath]);
            },
        };
    }

    /**
     * Opções padrão para cadastros
     */
    getDefaultCadastroOptions(navigationPath: string = '/'): {
        successMessage: string;
        errorMessage: string;
        delay: number;
        duration: number;
        onSuccess: () => void;
    } {
        return {
            successMessage: 'Cadastro realizado com sucesso!',
            errorMessage: 'Erro ao realizar cadastro',
            delay: 1500,
            duration: 3000,
            onSuccess: () => {
                this.router.navigate([navigationPath]);
            },
        };
    }

    /**
     * Opções padrão para checklists
     */
    getDefaultChecklistOptions(navigationPath: string = '/'): {
        successMessage: string;
        errorMessage: string;
        delay: number;
        duration: number;
        onSuccess: () => void;
    } {
        return {
            successMessage: 'Checklist salvo com sucesso!',
            errorMessage: 'Erro ao salvar checklist',
            delay: 1500,
            duration: 3000,
            onSuccess: () => {
                this.router.navigate([navigationPath]);
            },
        };
    }
}
